import { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import UpgradeModal from "@/components/UpgradeModal";
import UpgradePrompt from "@/components/UpgradePrompt";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, Info, TrendingUp, TrendingDown, DollarSign } from "lucide-react";

export default function Taxes() {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Check if user has access to tax overview
  const hasTaxAccess = user && (
    user.plan === 'pro' || 
    user.plan === 'pro_monthly' || 
    user.plan === 'pro_yearly'
  );
  
  // Show upgrade prompt if no access
  if (!hasTaxAccess) {
    return (
      <UpgradePrompt 
        feature={t('upgrade.taxCalculation', 'Steuerberechnung')}
        requiredPlan="Pro"
        currentPlan={user?.plan || 'Free'}
        description={t('upgrade.taxDescription', 'Automatische Berechnung Ihrer Steuerlast basierend auf Ihren Einnahmen und Ausgaben. Unterstützt DE, AT und CH.')}
      />
    );
  }
  const [taxData, setTaxData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [tradeTaxRate, setTradeTaxRate] = useState("3.5");
  const [country, setCountry] = useState("DE");

  useEffect(() => {
    // Get user's country
    if (user && user.country) {
      setCountry(user.country);
    }
    calculateTaxes();
  }, [user]);

  const calculateTaxes = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/tax/calculate?trade_tax_rate=${tradeTaxRate}`
      );
      setTaxData(response.data);
      if (response.data.country) {
        setCountry(response.data.country);
      }
    } catch (error) {
      console.error("Error calculating taxes:", error);
      toast.error("Fehler bei der Steuerberechnung");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  if (loading && !taxData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="taxes-page">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Steuerberechnung</h1>
        <p className="text-lg text-gray-600">
          {country === "AT" 
            ? "Vorläufige Steuerlast nach österreichischem Steuersystem" 
            : country === "DE"
            ? "Vorläufige Steuerlast nach deutschem Steuersystem"
            : "Steuerberechnung"}
        </p>
      </div>

      {/* Warning for unsupported countries (only OTHER, not CH) */}
      {country === "OTHER" && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-orange-900">
                <p className="font-semibold mb-2">
                  {t("taxes.noCalculation", "Automatische Steuerberechnung nicht verfügbar")}
                </p>
                <p className="mb-2">
                  {t("taxes.noCalculationText", "Für Ihr Land sind keine spezifischen Steuerberechnungen verfügbar.")}
                </p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>{t("taxes.manualEntry", "Bitte tragen Sie Ihre Steuern manuell in Ihre Buchhaltung ein")}</li>
                  <li>{t("taxes.consultTaxAdvisor", "Konsultieren Sie einen Steuerberater für genaue Berechnungen")}</li>
                  <li>{t("taxes.useGenericVAT", "Sie können die generischen Mehrwertsteuersätze in Rechnungen verwenden")}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Info for Switzerland - Canton required */}
      {country === "CH" && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-2">
                  🇨🇭 {t("taxes.swissTaxes", "Schweizer Steuerberechnung")}
                </p>
                <p className="mb-2">
                  {t("taxes.swissInfo", "Die Steuerberechnung berücksichtigt Bundessteuer, Kantonalsteuer und AHV/IV/EO-Beiträge.")}
                </p>
                <p className="text-xs text-blue-700">
                  {t("taxes.swissNote", "Hinweis: Bitte stellen Sie sicher, dass Ihr Kanton in den Einstellungen korrekt ausgewählt ist.")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Banner - General note for all supported countries */}
      {country && ["DE", "AT", "CH"].includes(country) && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Hinweis</p>
                <p>
                  Diese Berechnung dient nur als Orientierung und ersetzt keine
                  steuerliche Beratung. Für genaue Berechnungen und Steuererklärungen
                  konsultieren Sie bitte einen Steuerberater.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calculation Controls - Only for supported countries */}
      {country === "DE" && (
        <Card>
          <CardHeader>
            <CardTitle>Berechnungsparameter</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Gewerbesteuer Hebesatz (%)</Label>
                <Input
                  data-testid="trade-tax-rate-input"
                  type="number"
                  step="0.1"
                  value={tradeTaxRate}
                  onChange={(e) => setTradeTaxRate(e.target.value)}
                  placeholder="3.5"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Standard: 3.5% (entspricht 350% Hebesatz)
                </p>
              </div>
              <div className="flex items-end">
                <Button
                  data-testid="calculate-taxes-button"
                  onClick={calculateTaxes}
                  disabled={loading}
                  className="w-full"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  {loading ? "Berechne..." : "Neu berechnen"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {country === "AT" && (
        <Card>
          <CardHeader>
            <CardTitle>Berechnungsparameter</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              <Button
                data-testid="calculate-taxes-button"
                onClick={calculateTaxes}
                disabled={loading}
                className="w-full md:w-auto"
              >
                <Calculator className="h-4 w-4 mr-2" />
                {loading ? "Berechne..." : "Neu berechnen"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {country === "CH" && (
        <Card>
          <CardHeader>
            <CardTitle>🇨🇭 Schweizer Steuerberechnung</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              <Button
                data-testid="calculate-taxes-button"
                onClick={calculateTaxes}
                disabled={loading}
                className="w-full md:w-auto"
              >
                <Calculator className="h-4 w-4 mr-2" />
                {loading ? "Berechne..." : "Steuern berechnen"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {taxData && (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="card-hover">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Einnahmen
                </CardTitle>
                <TrendingUp className="h-5 w-5 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900" data-testid="total-income-display">
                  {formatCurrency(taxData.total_income)}
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Ausgaben
                </CardTitle>
                <TrendingDown className="h-5 w-5 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900" data-testid="total-expenses-display">
                  {formatCurrency(taxData.total_expenses)}
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Zu versteuerndes Einkommen
                </CardTitle>
                <DollarSign className="h-5 w-5 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900" data-testid="taxable-income-display">
                  {formatCurrency(taxData.taxable_income)}
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover border-2 border-emerald-500">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Nettogewinn
                </CardTitle>
                <Calculator className="h-5 w-5 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-emerald-600" data-testid="net-profit-display">
                  {formatCurrency(taxData.net_profit)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tax Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Steueraufschlüsselung</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Income Tax */}
                {/* Income Tax - Different display for CH */}
                {country === "CH" ? (
                  <>
                    {/* Federal Tax */}
                    {taxData.federal_tax > 0 && (
                      <div className="border-b pb-4">
                        <div className="flex justify-between items-center mb-2">
                          <div>
                            <h3 className="font-semibold text-gray-900">Bundessteuer</h3>
                            <p className="text-sm text-gray-600">
                              Direkte Bundessteuer 2025
                            </p>
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {formatCurrency(taxData.federal_tax)}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Cantonal Tax */}
                    {taxData.cantonal_tax > 0 && (
                      <div className="border-b pb-4">
                        <div className="flex justify-between items-center mb-2">
                          <div>
                            <h3 className="font-semibold text-gray-900">Kantonalsteuer</h3>
                            <p className="text-sm text-gray-600">
                              {taxData.canton_name || "Kanton/Gemeinde"}
                            </p>
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {formatCurrency(taxData.cantonal_tax)}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Total Income Tax for CH */}
                    <div className="border-b pb-4">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">Einkommensteuer Total</h3>
                          <p className="text-sm text-gray-600">
                            Bundessteuer + Kantonalsteuer
                          </p>
                        </div>
                        <div className="text-2xl font-bold text-gray-900" data-testid="income-tax-display">
                          {formatCurrency(taxData.income_tax)}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="border-b pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">Einkommensteuer</h3>
                        <p className="text-sm text-gray-600">
                          {country === "AT" 
                            ? "Nach österreichischem Einkommensteuertarif 2025" 
                            : "Nach deutschem Einkommensteuertarif 2024"}
                        </p>
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="income-tax-display">
                        {formatCurrency(taxData.income_tax)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Solidarity Surcharge - DE only */}
                {country === "DE" && taxData.solidarity_surcharge > 0 && (
                  <div className="border-b pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          Solidaritätszuschlag
                        </h3>
                        <p className="text-sm text-gray-600">
                          5,5% der Einkommensteuer (ab 17.543€ Einkommensteuer)
                        </p>
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="solidarity-surcharge-display">
                        {formatCurrency(taxData.solidarity_surcharge)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Trade Tax - DE only */}
                {country === "DE" && (
                  <div className="border-b pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">Gewerbesteuer</h3>
                        <p className="text-sm text-gray-600">
                          Freibetrag: 24.500€, Hebesatz: {tradeTaxRate}%
                        </p>
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="trade-tax-display">
                        {formatCurrency(taxData.trade_tax)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Social Security - AT and CH */}
                {(country === "AT" || country === "CH") && taxData.social_security > 0 && (
                  <div className="border-b pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {country === "CH" ? "Sozialversicherung (AHV/IV/EO)" : "Sozialversicherung (SVS)"}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {country === "CH" 
                            ? "10% vom Nettoeinkommen (AHV/IV/EO für Selbstständige)"
                            : "Ab 6.613,20€ Einkommen (ca. 25%: Kranken- & Pensionsversicherung)"}
                        </p>
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="social-security-display">
                        {formatCurrency(taxData.social_security)}
                      </div>
                    </div>
                  </div>
                )}

                {/* VAT */}
                <div className="pb-4">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Umsatzsteuer (Vorsteuer)
                      </h3>
                      <p className="text-sm text-gray-600">
                        Eingenommene USt: {formatCurrency(taxData.vat_collected)} | 
                        Gezahlte USt: {formatCurrency(taxData.vat_paid)}
                      </p>
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-testid="vat-payable-display">
                      {formatCurrency(taxData.vat_payable)}
                    </div>
                  </div>
                </div>

                {/* Total Tax Burden */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        Gesamte Steuerlast
                      </h3>
                      <p className="text-sm text-gray-600">
                        {country === "CH" 
                          ? "Bundessteuer + Kantonalsteuer + AHV/IV/EO + MwSt"
                          : country === "AT"
                          ? "Einkommensteuer + SVS + MwSt"
                          : "Einkommensteuer + Solidaritätszuschlag + Gewerbesteuer + USt"}
                      </p>
                    </div>
                    <div className="text-3xl font-bold text-red-600" data-testid="total-tax-burden-display">
                      {formatCurrency(taxData.total_tax_burden)}
                    </div>
                  </div>
                </div>

                {/* Effective Tax Rate */}
                <div className="bg-emerald-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-bold text-emerald-900">
                        Effektiver Steuersatz
                      </h3>
                      <p className="text-sm text-emerald-700">
                        Verhältnis der Steuerlast zum zu versteuernden Einkommen
                      </p>
                    </div>
                    <div className="text-3xl font-bold text-emerald-600" data-testid="effective-tax-rate-display">
                      {taxData.taxable_income > 0
                        ? ((taxData.total_tax_burden / taxData.taxable_income) * 100).toFixed(2)
                        : "0.00"}
                      %
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tax Optimization Tips */}
          <Card>
            <CardHeader>
              <CardTitle>Tipps zur Steueroptimierung</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-start space-x-2">
                  <div className="h-2 w-2 bg-emerald-600 rounded-full mt-1.5 flex-shrink-0"></div>
                  <p>
                    <strong>Betriebsausgaben erfassen:</strong> Alle betrieblichen
                    Ausgaben können steuerlich geltend gemacht werden (Büromaterial,
                    Software, Weiterbildungen, etc.)
                  </p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="h-2 w-2 bg-emerald-600 rounded-full mt-1.5 flex-shrink-0"></div>
                  <p>
                    <strong>Abschreibungen nutzen:</strong> Größere Anschaffungen wie
                    Computer oder Fahrzeuge können über mehrere Jahre abgeschrieben
                    werden
                  </p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="h-2 w-2 bg-emerald-600 rounded-full mt-1.5 flex-shrink-0"></div>
                  <p>
                    <strong>Kleinunternehmerregelung prüfen:</strong> Bei Umsätzen unter
                    22.000€ kann die Kleinunternehmerregelung vorteilhaft sein (keine
                    Umsatzsteuer)
                  </p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="h-2 w-2 bg-emerald-600 rounded-full mt-1.5 flex-shrink-0"></div>
                  <p>
                    <strong>Altersvorsorge:</strong> Beiträge zur Altersvorsorge können
                    steuerlich absetzbar sein
                  </p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="h-2 w-2 bg-emerald-600 rounded-full mt-1.5 flex-shrink-0"></div>
                  <p>
                    <strong>Steuerberater konsultieren:</strong> Ein Steuerberater kann
                    individuelle Optimierungsmöglichkeiten aufzeigen
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
